import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import LineHeader from './components/LineHeader';
import LineContent from './components/LineContent';
import AudioManager from './components/AudioManager';
import GlossaryAssignment from './components/GlossaryAssignment';
import CompletionStatus from './components/CompletionStatus';
import ActionButtons from './components/ActionButtons';
import { getLineById } from '../../services/playsService';
import { completeAudioUpload, getLineAudioRecordings, deleteAudioRecording, updateAudioRecording } from '../../services/audioManagementService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const LineEditingModal = () => {
  const location = useLocation();
  const { lineId, playId, playTitle } = location?.state || {};
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lineData, setLineData] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [selectedWords, setSelectedWords] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [recordedBy, setRecordedBy] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await userService?.getAll();
        setAvailableUsers(users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLineData = async () => {
      if (!lineId) {
        setError('No line ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await getLineById(lineId);
        setLineData(data);
        
        // Set initial selected words from glossary assignments
        if (data?.glossaryTerms && data?.glossaryTerms?.length > 0) {
          setSelectedWords(data?.glossaryTerms);
        }

        // Fetch audio recordings for this line
        const audioRecordings = await getLineAudioRecordings(data?.id);
        if (audioRecordings && audioRecordings?.length > 0) {
          // Use the most recent audio recording
          const latestRecording = audioRecordings?.[0];
          setAudioFile({
            id: latestRecording?.id,
            name: `Audio Recording`,
            url: latestRecording?.signedUrl,
            size: latestRecording?.duration_seconds,
            isExisting: true
          });
          setRecordedBy(latestRecording?.user_id || null);
        }
      } catch (err) {
        console.error('Error fetching line data:', err);
        setError(err?.message || 'Failed to load line data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLineData();
  }, [lineId]);

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmClose) return;
    }
    navigate('/edit-play', { state: { playId: lineData?.playId } });
  };

  const handleNavigate = (direction) => {
    if (hasChanges) {
      const confirmNavigate = window.confirm('You have unsaved changes. Save before navigating?');
      if (confirmNavigate) {
        handleSave();
      }
    }
    console.log(`Navigating to ${direction} line`);
    // TODO: Implement actual navigation to previous/next line
  };

  const handleWordSelect = (words) => {
    setSelectedWords(words);
    setHasChanges(true);
  };

  const handleAudioUpload = async (file) => {
    try {
      setIsUploadingAudio(true);
      setError(null);

      // Upload to Supabase Storage and create database entry
      const result = await completeAudioUpload(file, lineData?.id, recordedBy || user?.id);

      // Update local state with uploaded file info
      setAudioFile({
        id: result?.audioRecording?.id,
        name: file?.name,
        url: result?.uploadResult?.signedUrl,
        size: file?.size,
        isExisting: true
      });

      // Update recordedBy state to match what was saved in database
      setRecordedBy(result?.audioRecording?.user_id || null);

      setHasChanges(false); // Audio is already saved
      alert('Audio file uploaded successfully!');
    } catch (err) {
      console.error('Error uploading audio:', err);
      setError(err?.message || 'Failed to upload audio file');
      alert(`Failed to upload audio: ${err?.message}`);
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const handleRecordedByChange = async (userId) => {
    setRecordedBy(userId);

    // If audio file already exists, update the database immediately
    if (audioFile?.id && userId) {
      try {
        await updateAudioRecording(audioFile?.id, { userId });
        console.log('Recorded by user updated successfully!');
      } catch (err) {
        console.error('Error updating recorded by:', err);
        alert(`Failed to update: ${err?.message}`);
      }
    }
  };

  const handleAudioDelete = async () => {
    if (!audioFile?.id) {
      // If it's a new file not yet uploaded, just clear local state
      setAudioFile(null);
      setHasChanges(true);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this audio file?')) {
      return;
    }

    try {
      setIsUploadingAudio(true);
      setError(null);

      // Delete from database and storage
      await deleteAudioRecording(audioFile?.id);

      setAudioFile(null);
      setHasChanges(false);
      alert('Audio file deleted successfully!');
    } catch (err) {
      console.error('Error deleting audio:', err);
      setError(err?.message || 'Failed to delete audio file');
      alert(`Failed to delete audio: ${err?.message}`);
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const handleAssignGlossary = async (words, termId) => {
    try {
      setIsSaving(true);
      setError(null);

      // For each selected word, create a glossary assignment
      const assignments = words?.map(wordKey => ({
        line_id: lineData?.id,
        glossary_term_id: termId,
        word_key: wordKey
      }));

      // Insert assignments into database
      const { error: assignError } = await supabase?.from('line_glossary_assignments')?.upsert(assignments, { onConflict: 'line_id,word_key' });

      if (assignError) throw assignError;

      // Refresh line data to get updated glossary terms
      const updatedData = await getLineById(lineId);
      setLineData(updatedData);
      
      setHasChanges(false);
      alert('Glossary terms assigned successfully!');
    } catch (err) {
      console.error('Error assigning glossary:', err);
      setError(err?.message || 'Failed to assign glossary terms');
      alert(`Failed to assign glossary: ${err?.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateNewTerm = async (newTerm) => {
    try {
      setIsSaving(true);
      setError(null);

      // Insert new glossary term
      const { data: createdTerm, error: createError } = await supabase?.from('glossary_terms')?.insert([{
          word: newTerm?.word,
          definition: newTerm?.definition,
          pronunciation: newTerm?.pronunciation || null,
          play_id: lineData?.playId
        }])?.select()?.single();

      if (createError) throw createError;

      // Assign the new term to selected words
      if (createdTerm && selectedWords?.length > 0) {
        await handleAssignGlossary(selectedWords, createdTerm?.id);
      }

      alert('New glossary term created and assigned!');
    } catch (err) {
      console.error('Error creating glossary term:', err);
      setError(err?.message || 'Failed to create glossary term');
      alert(`Failed to create glossary term: ${err?.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setHasChanges(false);
      alert('Changes saved successfully!');
    }, 1500);
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmCancel = window.confirm('Discard unsaved changes?');
      if (!confirmCancel) return;
    }
    handleClose();
  };

  const customBreadcrumbs = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Play Management', path: '/play-management' },
    { label: lineData?.playTitle || 'Play', path: '/edit-play', state: { playId: lineData?.playId } },
    { label: 'Line Editing', path: '/line-editing-modal' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation
          user={user}
          isAuthenticated={true}
          notifications={[]}
        />
        <div className="main-content">
          <BreadcrumbTrail customBreadcrumbs={customBreadcrumbs} />
          <div className="fixed inset-0 bg-foreground/50 z-[2000] flex items-center justify-center p-4" style={{ marginTop: '64px' }}>
            <div className="bg-background rounded-lg shadow-2xl p-8">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-lg text-foreground">Loading line data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation
          user={user}
          isAuthenticated={true}
          notifications={[]}
        />
        <div className="main-content">
          <BreadcrumbTrail customBreadcrumbs={customBreadcrumbs} />
          <div className="fixed inset-0 bg-foreground/50 z-[2000] flex items-center justify-center p-4" style={{ marginTop: '64px' }}>
            <div className="bg-background rounded-lg shadow-2xl p-8 max-w-md">
              <div className="text-center">
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <h2 className="text-xl font-bold text-foreground mb-2">Error Loading Line</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <button
                  onClick={() => navigate('/play-management')}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Back to Play Management
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={user}
        isAuthenticated={true}
        notifications={[]}
      />
      <div className="main-content">
        <BreadcrumbTrail customBreadcrumbs={customBreadcrumbs} />

        <div className="fixed inset-0 bg-foreground/50 z-[2000] flex items-center justify-center p-4" style={{ marginTop: '64px' }}>
          <div className="bg-background rounded-lg shadow-2xl w-full max-w-5xl max-h-[calc(100vh-128px)] overflow-hidden flex flex-col">
            <LineHeader
              lineData={lineData}
              onClose={handleClose}
              onNavigate={handleNavigate}
            />

            <div className="flex-1 overflow-y-auto">
              <LineContent
                lineData={lineData}
                selectedWords={selectedWords}
                onWordSelect={handleWordSelect}
                assignedGlossaryTerms={lineData?.assignedGlossaryTerms || []}
              />

              <AudioManager
                lineData={lineData}
                audioFile={audioFile}
                onAudioUpload={handleAudioUpload}
                onAudioDelete={handleAudioDelete}
                isUploading={isUploadingAudio}
                recordedBy={recordedBy}
                onRecordedByChange={handleRecordedByChange}
                availableUsers={availableUsers}
              />

              <GlossaryAssignment
                selectedWords={selectedWords}
                glossaryTerms={lineData?.glossaryTerms || []}
                onAssignGlossary={handleAssignGlossary}
                onCreateNew={handleCreateNewTerm}
              />

              <CompletionStatus
                hasAudio={!!audioFile}
                hasGlossary={selectedWords?.length > 0}
                isReviewed={false}
                lineData={lineData}
              />
            </div>

            <ActionButtons
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={isSaving}
              hasChanges={hasChanges}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineEditingModal;